import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { noop } from '../lib/serviceHelpers';

export const useFetchPartners = (params = {}, opts = {}) => {
  return useQuery(
    ['partners', params],
    async () => {
      const res = await axios.get(`/partners`, { params });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );
};

export const useFetchPartner = (partnerId, opts = {}) => {
  return useQuery(
    ['partners', partnerId],
    async () => {
      const res = await axios.get(`/partners/${partnerId}`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );
};

export const useCheckPartnerUrl = (callback, opts = {}) => {
  return useMutation(
    async (url) => {
      const res = await axios.post(`/auth/check-url`, {
        url,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useCreatePartner = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/partners`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdatePartner = (callback, opts = {}) => {
  return useMutation(
    async ({ partnerId, payload }) => {
      const res = await axios.put(`/partners/${partnerId}`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeletePartner = (callback, opts = {}) => {
  return useMutation(
    async (partnerId) => {
      const res = await axios.delete(`/partners/${partnerId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// --- Review queue (individual referral-partner applications) ---

export const useFetchReviewQueue = (page = 1, opts = {}) => {
  return useQuery(
    ['partners-review-queue', { page }],
    async () => {
      const res = await axios.get(`/partners/review-queue`, {
        params: { page },
      });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );
};

// Review-queue mutations touch both the queue and the partners list (approving
// creates a partner row), so every one invalidates both key families.
const useReviewMutation = (mutationFn, callback, opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    ...opts,
    onSuccess: (...args) => {
      queryClient.invalidateQueries(['partners']);
      queryClient.invalidateQueries(['partners-review-queue']);
      (callback || noop)(...args);
    },
  });
};

export const useApprovePayee = (callback, opts = {}) =>
  useReviewMutation(
    async (payeeId) => {
      const res = await axios.post(`/partners/payees/${payeeId}/approve`);
      return res.data;
    },
    callback,
    opts,
  );

export const useFollowUpPayee = (callback, opts = {}) =>
  useReviewMutation(
    async ({ payeeId, note }) => {
      const res = await axios.post(`/partners/payees/${payeeId}/follow-up`, {
        note,
      });
      return res.data;
    },
    callback,
    opts,
  );

export const useRejectPayee = (callback, opts = {}) =>
  useReviewMutation(
    async ({ payeeId, note }) => {
      const res = await axios.post(`/partners/payees/${payeeId}/reject`, {
        note,
      });
      return res.data;
    },
    callback,
    opts,
  );

export const useSendPayeePacket = (callback, opts = {}) =>
  useReviewMutation(
    async (payeeId) => {
      const res = await axios.post(`/partners/payees/${payeeId}/send-packet`);
      return res.data;
    },
    callback,
    opts,
  );

export const useUploadPayeePaperwork = (callback, opts = {}) =>
  useReviewMutation(
    async ({ payeeId, files }) => {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await axios.post(
        `/partners/payees/${payeeId}/paperwork-upload`,
        fd,
      );
      return res.data;
    },
    callback,
    opts,
  );

export const useUploadPartnerPaperwork = (callback, opts = {}) =>
  useReviewMutation(
    async ({ partnerId, files }) => {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await axios.post(
        `/partners/${partnerId}/paperwork-upload`,
        fd,
      );
      return res.data;
    },
    callback,
    opts,
  );
